import api from "./client";

/**
 * Currencies an integration may be configured in, as ISO 4217 codes.
 *
 * Must stay in step with SUPPORTED_CURRENCIES in the API's platforms.constant.ts
 * — the server rejects anything outside its own list, so a code added only here
 * would fail validation on save.
 *
 * The label carries the symbol purely so the dropdown is readable. Nothing in
 * the app converts between currencies; this records what a price already is.
 */
export const SUPPORTED_CURRENCIES = [
  { code: "USD", label: "USD — US Dollar ($)" },
  { code: "EUR", label: "EUR — Euro (€)" },
  { code: "GBP", label: "GBP — Pound Sterling (£)" },
  { code: "INR", label: "INR — Indian Rupee (₹)" },
  { code: "CAD", label: "CAD — Canadian Dollar ($)" },
] as const;

export const DEFAULT_CURRENCY = "USD";

/** One row of an order's history. */
export interface OrderActivity {
  id: string;
  action: string;
  summary: string;
  shipmentId: string | null;
  trackingNumbers: string[];
  details: Record<string, any> | null;
  by: string | null;
  email: string | null;
  at: string;
}

/**
 * One message on the store's order: the checkout note, a buyer message the
 * marketplace connector relayed, or a comment the store team added.
 * `holdApplied` marks the message that put the order on hold here.
 */
export interface OrderCommunication {
  id: string;
  source: 'CHECKOUT_NOTE' | 'BUYER_MESSAGE' | 'ORDER_COMMENT' | 'NIXOOK_COMMENT' | string;
  message: string;
  /** Who wrote it, for comments written here. Store messages carry no author. */
  authorName: string | null;
  authorEmail: string | null;
  /** The teammate the comment was emailed to, if the writer picked one. */
  notifiedName: string | null;
  notifiedEmail: string | null;
  storeStatus: string | null;
  isCancelSignal: boolean;
  matchedKeyword: string | null;
  /**
   * A store message that needs a person short of cancelling: 'return',
   * 'refund', 'exchange', 'damaged', 'missing', 'wrong item', 'customer', or
   * 'buyer message' for anything the buyer wrote. Null for an ordinary store
   * message, which never lights the comment badge.
   */
  attentionKeyword: string | null;
  holdApplied: boolean;
  /** When it was read and by whom; null while it still counts as unread. */
  readAt: string | null;
  readByName: string | null;
  readByEmail: string | null;
  postedAt: string | null;
  receivedAt: string;
}

/**
 * Does this message count towards the unread badge until it is read? A
 * comment written here waits for the teammate it was emailed to, or for
 * anyone when it was emailed to no one; a cancel request or a store message
 * flagged for attention waits for anyone.
 */
export const needsReading = (c: Pick<OrderCommunication, 'source' | 'isCancelSignal' | 'attentionKeyword'>): boolean =>
  c.source === 'NIXOOK_COMMENT' || c.isCancelSignal || !!c.attentionKeyword;

/** A teammate a comment can be emailed to. */
export interface CommentRecipient {
  id: string;
  name: string;
  email: string;
}

/** What a sync of every store connection answers: the total, and each store's own result. */
export interface SyncAllResult {
  count: number;
  integrations: {
    id: string;
    name: string;
    platform: string;
    count: number;
    skipped?: string;
    error?: string;
  }[];
}

/** What Sync Comments answers: how many orders were asked about and what was new. */
export interface CommentSyncResult {
  checked: number;
  messagesAdded: number;
  ordersWithNew: number;
  held: number;
  notFound: number;
  /** Active stores whose orders were read (Magento today). */
  stores: number;
  /** Active stores whose orders cannot be re-read by id (Magento only today). */
  skippedStores: string[];
  errors: string[];
}

export const orderIntegrations = {
  findAll: async (companyId?: string) => {
    const response = await api.get("/order-integrations", { params: { companyId } });
    return response.data;
  },
  findOne: async (id: string) => {
    const response = await api.get(`/order-integrations/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post("/order-integrations", data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.patch(`/order-integrations/${id}`, data);
    return response.data;
  },
  remove: async (id: string) => {
    const response = await api.delete(`/order-integrations/${id}`);
    return response.data;
  },
  /** Attribute codes the store actually exposes, for the cost-attribute picker. */
  listAttributes: async (
    id: string,
  ): Promise<Array<{ code: string; sample: string | null; numeric: boolean }>> => {
    const response = await api.get(`/order-integrations/${id}/attributes`);
    return response.data;
  },
  /**
   * Verify an integration's credentials.
   *
   * `candidate` tests credentials that have been typed but not saved — the edit
   * page passes what is in the form so a token can be proved before it replaces
   * the stored one. Omitted, this tests what is already stored.
   *
   * skipSuccessCheck because a failed test is a 200 carrying `success: false`,
   * which the shared interceptor would otherwise turn into a toast and a
   * rejected promise, leaving the caller unable to read the result.
   */
  testConnection: async (
    id: string,
    candidate?: { credentials?: any; storeUrl?: string },
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/order-integrations/${id}/test`,
      candidate ?? {},
      { skipSuccessCheck: true },
    );
    return response.data;
  },
  /** Labels this store has not got yet (Send Tracking's confirm). */
  unsentTracking: async (
    id: string,
  ): Promise<{ count: number; placeholderNeeded?: Array<{ id: string; orderNumber: string | null }> }> => {
    const response = await api.get(`/order-integrations/${id}/send-tracking`);
    return response.data;
  },
  /** Send those labels to the store now, whatever the Platform Sync Trigger. */
  sendTracking: async (
    id: string,
  ): Promise<{ total: number; sent: number; failed: Array<{ orderNumber: string; trackingNumber: string; reason: string }> }> => {
    const response = await api.post(`/order-integrations/${id}/send-tracking`);
    return response.data;
  },
  syncOrders: async (id: string): Promise<{ count: number; skipped?: string }> => {
    const response = await api.post(`/order-integrations/${id}/sync`);
    return response.data;
  },
  /**
   * Sync orders from every active store connection of the company now, as the
   * scheduled sync does, and say what each store returned.
   */
  syncAll: async (): Promise<SyncAllResult> => {
    const response = await api.post('/order-integrations/sync-all');
    return response.data;
  },
  /**
   * Re-read the store's comments on the newest orders (how many is the Order
   * Sync setting "Orders checked by Sync Comments") and store the new ones.
   */
  syncComments: async (): Promise<CommentSyncResult> => {
    const response = await api.post('/order-integrations/sync-comments');
    return response.data;
  },
  getAllOrders: async (paramsOrCompanyId?: any) => {
    let params: any = {};
    if (typeof paramsOrCompanyId === 'string') {
      params = { companyId: paramsOrCompanyId };
    } else if (paramsOrCompanyId) {
      params = paramsOrCompanyId;
    }
    const response = await api.get("/order-integrations/all-orders", { params });
    return response.data;
  },
  /**
   * Values for the orders-grid filter dropdowns.
   *
   * Separate from getAllOrders because the server computes these by scanning
   * the whole orders table; bundling them into the list response meant paying
   * for that scan on every page click, sort and keystroke. Fetch once per visit.
   */
  getOrderFilterOptions: async (companyId?: string) => {
    const response = await api.get("/order-integrations/order-filter-options", {
      params: companyId ? { companyId } : {},
    });
    return response.data as {
      uniqueStatuses: string[];
      uniquePlatforms: string[];
      uniqueSources: string[];
    };
  },
  /** Tag filter options (User tags that held SKUs) and which are the caller's today. */
  getOrderTagOptions: async () => {
    const response = await api.get("/order-integrations/order-tag-options");
    return response.data as { tags: { id: string; name: string }[]; myTagIds: string[] };
  },
  /** Legacy: auto-label entire order */
  autoCreateLabel: async (orderId: string) => {
    const response = await api.post(`/order-integrations/orders/${orderId}/auto-label`);
    return response.data;
  },
  getOrder: async (orderId: string) => {
    const response = await api.get(`/order-integrations/orders/${orderId}`);
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Partial shipment endpoints
  // ---------------------------------------------------------------------------

  /** Returns shipment history + remaining quantities for an order */
  getShipmentStatus: async (orderId: string) => {
    const response = await api.get(`/order-integrations/orders/${orderId}/shipment-status`);
    return response.data;
  },

  /** Refreshes dimensions from platform and returns updated shipment status */
  /**
   * Correct an order's shipping address by hand.
   *
   * Partial by design — send only the fields being changed. The server merges
   * onto the stored address, so an omitted field keeps its value rather than
   * being blanked, and the caller never has to echo back a copy of something it
   * read before someone else edited it.
   *
   * Local only: this does not reach the store. The server marks the order so
   * the address refresh pass will not overwrite the correction.
   */
  updateShippingAddress: async (
    orderId: string,
    address: {
      name?: string;
      street1?: string;
      street2?: string;
      city?: string;
      state?: string;
      zip?: string;
      country?: string;
      phone?: string;
      deliveryNotes?: string;
    },
  ) => {
    const response = await api.patch(
      `/order-integrations/orders/${orderId}/shipping-address`,
      address,
    );
    return response.data;
  },
  /**
   * Re-read one order's shipping address from the store.
   *
   * Overwrites a local correction — `hadLocalEdit` in the response says whether
   * it did, but the confirmation belongs before the call, not after it.
   */
  refreshOrderAddress: async (
    orderId: string,
  ): Promise<{
    changed: boolean;
    found: boolean;
    hadLocalEdit: boolean;
    fields: string[];
    skipped?: string;
    address?: any;
  }> => {
    const response = await api.post(
      `/order-integrations/orders/${orderId}/refresh-address`,
    );
    return response.data;
  },
  /**
   * Re-read this order's marketplace fee from the store. The channel connector
   * adds an Amazon fee to the Magento order days later without marking it
   * changed, so the routine sync never brings it in. Changes the fee only.
   */
  refreshOrderFee: async (
    orderId: string,
  ): Promise<{
    changed: boolean;
    found: boolean;
    fee: number;
    previousFee: number;
    skipped?: string;
  }> => {
    const response = await api.post(`/order-integrations/orders/${orderId}/refresh-fee`);
    return response.data;
  },
  refreshOrderDimensions: async (orderId: string) => {
    const response = await api.post(`/order-integrations/orders/${orderId}/refresh-dims`);
    return response.data;
  },

  /** Get carrier rates for a subset of items */
  getPartialShipmentRates: async (orderId: string, items: Array<{ sku: string; qty: number; length?: number; width?: number; height?: number }>, customPackages?: any[], facilityId?: string) => {
    const response = await api.post(`/order-integrations/orders/${orderId}/partial-shipment/rates`, { items, customPackages, facilityId });
    return response.data;
  },

  /** Auto-label: cheapest carrier for selected items */
  createPartialShipmentAuto: async (orderId: string, items: Array<{ sku: string; qty: number; length?: number; width?: number; height?: number }>, customPackages?: any[], facilityId?: string) => {
    const response = await api.post(`/order-integrations/orders/${orderId}/partial-shipment/auto`, { items, customPackages, facilityId });
    return response.data;
  },

  /** Manual label: user-chosen carrier/service for selected items */
  createPartialShipmentManual: async (
    orderId: string,
    items: Array<{ sku: string; qty: number; length?: number; width?: number; height?: number }>,
    carrier: string,
    serviceType: string,
    quotationRate: any,
    customPackages?: any[],
    /**
     * The rate list as it was on screen when this carrier was picked, so the
     * choice can be compared against later.
     *
     * Sent from here rather than re-fetched server-side: a second rate shop
     * would bill another round of carrier calls and could come back with
     * different prices than the ones actually chosen between.
     */
    ratesOnScreen?: { rates?: Record<string, any[]>; errors?: any[] },
    /** Ship-from facility: from-address, shipment assignment and auto before-pack approval. */
    facilityId?: string
  ) => {
    const response = await api.post(`/order-integrations/orders/${orderId}/partial-shipment/manual`, {
      items,
      carrier,
      serviceType,
      quotationRate,
      customPackages,
      ratesOnScreen,
      facilityId,
    });
    return response.data;
  },

  /** Auto-label all remaining (unshipped) items */
  autoLabelRemaining: async (orderId: string) => {
    const response = await api.post(`/order-integrations/orders/${orderId}/auto-label-remaining`);
    return response.data;
  },
  /** Add manual tracking for selected items */
  createManualTracking: async (
    orderId: string,
    data: {
      trackingNumber: string;
      carrier?: string;
      serviceName?: string;
      source?: string;
      externalSystemName?: string;
      items: Array<{ item_id?: number; sku: string; qty: number }>;
      boxId?: string;
      customBox?: { name?: string; length?: number; width?: number; height?: number };
      weight?: number;
      weightUnit?: string;
      cost?: number;
      labelFile?: { base64: string; filename: string; mimeType: string };
    },
  ) => {
    const response = await api.post(`/order-integrations/orders/${orderId}/manual-tracking`, data);
    return response.data;
  },
  /** Generate a standalone PDF without creating shipment/tracking records. */
  generateManualPackingSlip: async (
    orderId: string,
    data: {
      items: Array<{ item_id?: number; sku: string; qty: number }>;
      boxId?: string;
      customBox?: { name?: string; length: number; width: number; height: number };
    },
  ) => {
    const response = await api.post(`/order-integrations/orders/${orderId}/manual-packing-slip`, data);
    return response.data as { pdfBase64: string; filename: string };
  },
  /** The order's history, newest first: labels, voids, manual changes, paperwork. */
  getOrderActivity: async (orderId: string): Promise<OrderActivity[]> => {
    const response = await api.get(`/order-integrations/orders/${orderId}/activity`);
    return response.data;
  },
  /** The order's messages from the store, newest first: checkout note, buyer messages, admin comments. */
  getOrderCommunications: async (orderId: string): Promise<OrderCommunication[]> => {
    const response = await api.get(`/order-integrations/orders/${orderId}/communications`);
    return response.data;
  },
  /** Teammates a comment can be emailed to: this company's active users. */
  /**
   * The viewer has the order's comments open. Marks read the ones that were
   * theirs to read: any emailed to no one or to them, and none of their own.
   */
  markCommunicationsRead: async (orderId: string): Promise<{ marked: number }> => {
    const response = await api.post(`/order-integrations/orders/${orderId}/communications/read`);
    return response.data;
  },
  getCommentRecipients: async (): Promise<CommentRecipient[]> => {
    const response = await api.get('/order-integrations/comment-recipients');
    return response.data;
  },
  /**
   * Add a comment to the order's stack. Stays in Nixook; it is not sent to the
   * store. With `notifyUserId`, the comment is also emailed to that teammate.
   */
  addOrderComment: async (
    orderId: string,
    message: string,
    notifyUserId?: string | null,
  ): Promise<OrderCommunication> => {
    const response = await api.post(`/order-integrations/orders/${orderId}/communications`, {
      message,
      notifyUserId: notifyUserId || null,
    });
    return response.data;
  },
  /**
   * Set an order's status by hand (admin tier, `orders.edit_status`). Local
   * only: the store is not told. `lastError` comes back cleared when the new
   * status carries no reason.
   */
  updateOrderStatus: async (
    orderId: string,
    status: string,
  ): Promise<{ changed: boolean; from: string | null; status: string; lastError: string | null }> => {
    const response = await api.patch(`/order-integrations/orders/${orderId}/status`, { status });
    return response.data;
  },
  deleteOrder: async (orderId: string) => {
    const response = await api.delete(`/order-integrations/orders/${orderId}`);
    return response.data;
  },
};

/**
 * Platforms with an order connector.
 *
 * Mirrors ORDER_PLATFORMS in the API's common/platforms/platforms.constant.ts,
 * which is the source of truth — the two repos cannot share a module. Used to
 * hide the Test and Sync actions on platforms that supply product data only,
 * rather than offering a button that can never succeed.
 */
export const ORDER_PLATFORMS = ['Magento', 'WooCommerce', 'OpenCart'];

export const supportsOrders = (platform: string): boolean =>
  ORDER_PLATFORMS.includes(platform);
